import React, { useState, useEffect, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Canvas3D from './Canvas3D';
import FurosList from './FurosList';
import FuroManual from './FuroManual';
import RasgoManual from './RasgoManual';
import RegrasEspeciaisSection from './RegrasEspeciaisSection';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import ConfirmDialog from '../ui/ConfirmDialog';
import useConfirm from '../ui/useConfirm';
import './EditorMPR.css';
import { exportarMPR, parseMPR, gerarPDFEditor, gerarPDFUsinagem, agruparFuros, previewPaginas, sugerirPosicionamento, aceitarPosicionamento, upsertBordas } from '../../services/api';
import api from '../../services/api';
import { inverterFuro, inverterRasgo, sincronizarFuroOriginal, computarTransformacaoPura } from './transformacoesFuro';

const _uid = () => uuidv4().replace(/-/g, '');

// Mapeia posição visual → chave canônica de borda, por rotação.
// 90° horário: esquerda→topo, topo→direita, direita→baixo, baixo→esquerda (canonical→visual).
// A tabela armazena o inverso: visual→canonical.
const _VISUAL_TO_CANONICAL_BORDA = {
  0:   { topo: 'topo',     baixo: 'baixo',    esquerda: 'esquerda', direita: 'direita'  },
  90:  { topo: 'esquerda', baixo: 'direita',  esquerda: 'baixo',    direita: 'topo'     },
  180: { topo: 'baixo',    baixo: 'topo',     esquerda: 'direita',  direita: 'esquerda' },
  270: { topo: 'direita',  baixo: 'esquerda', esquerda: 'topo',     direita: 'baixo'    },
};

function EditorMPR({ pecaInicial, onVoltar, onPecaAtualizada }) {
  // ========================================
  // ESTADOS
  // ========================================
  const [peca, setPeca] = useState({
    nome: '',
    largura: 0,
    comprimento: 0,
    espessura: 15,
    furos: [],
    furosHorizontais: [],
    rasgos: []
  });

  const [bordas, setBordas] = useState({
    topo: 'nenhum',
    baixo: 'nenhum',
    esquerda: 'nenhum',
    direita: 'nenhum'
  });

  const [codigoPeca, setCodigoPeca] = useState('');
  const [loading, setLoading] = useState(false);
  // alertas: { [titulo_pagina]: texto } — configurado por página no modal
  const [alertas, setAlertas] = useState({});
  // modal de alertas: null = fechado, array de {titulo, tipo} = aberto
  const [modalAlertas, setModalAlertas] = useState(null);
  // rascunho editado dentro do modal (confirmado ao clicar Gerar PDF)
  const [draftAlertas, setDraftAlertas] = useState({});
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [selectedFuro, setSelectedFuro] = useState(null);
  const [transformacao, setTransformacao] = useState({ rotacao: 0, espelhado: false, invertido: false });
  const [pecaOriginal, setPecaOriginal] = useState(null);
  // backup de rascunho aguardando decisão de recuperar/descartar (null = sem dialog)
  const [backupRecuperavel, setBackupRecuperavel] = useState(null);
  const [confirmEl, confirm] = useConfirm();
  // import de .mpr pendente de confirmação — só é preenchido quando a peça
  // atual tem rasgos que a substituição vai descartar (ver handleImportarMPR)
  const [importPendente, setImportPendente] = useState(null);
  const [versao, setVersao] = useState('00');
  const [docsFuracao, setDocsFuracao]   = useState(true);
  const [docsUsinagem, setDocsUsinagem] = useState(false);

  const [regrasEspeciais, setRegrasEspeciais] = useState([]);
  const [selectedFuros, setSelectedFuros] = useState(new Set());
  const [hoverRegra, setHoverRegra] = useState(null);
  const [rasgoEditandoIndex, setRasgoEditandoIndex] = useState(null);

  // Posicionamento automático
  const [planoRecomendado, setPlanoRecomendado] = useState(null);
  const [passadaIdx, setPassadaIdx] = useState(0);
  const [loadingRecomendado, setLoadingRecomendado] = useState(false);
  const [erroRecomendado, setErroRecomendado] = useState(null);
  const [notaManual, setNotaManual] = useState(false);
  const [bordaBatenteForcada, setBordaBatenteForcada] = useState(null);

  // Larguras dos painéis (para resize)
  const [sidebarWidth, setSidebarWidth] = useState(260);
  const [panelHeight, setPanelHeight] = useState(280);

  // Clipboard para CTRL+C / CTRL+V
  const [clipboard, setClipboard] = useState(null);

  // Painel inferior — abas
  const [activeTab, setActiveTab] = useState('config');
  const prevTabRef = useRef('config');

  // Replicação (substituindo getElementById)
  const [replicarQtd, setReplicarQtd] = useState('');
  const [replicarDist, setReplicarDist] = useState('');
  const [replicarDir, setReplicarDir] = useState('x');

  // Auto-abrir aba de edição ao selecionar um furo
  useEffect(() => {
    if (selectedFuro) {
      prevTabRef.current = activeTab === 'edit' ? prevTabRef.current : activeTab;
      setActiveTab('edit');
    } else if (activeTab === 'edit') {
      setActiveTab(prevTabRef.current);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFuro]);

  // Auto-abrir aba de rasgo ao selecionar um rasgo na lista
  useEffect(() => {
    if (rasgoEditandoIndex !== null) {
      setActiveTab('rasgo');
    }
  }, [rasgoEditandoIndex]);

  // ========================================
// AUTO-SAVE LOCAL (backup de emergência)
// ========================================

// Salvar no localStorage a cada alteração
useEffect(() => {
  if (peca.comprimento && peca.largura) {
    const backup = {
      peca,
      pecaOriginal,
      bordas,
      transformacao,
      alertas,
      codigoPeca,
      timestamp: Date.now()
    };
    localStorage.setItem('corewood_editor_backup', JSON.stringify(backup));
  }
}, [peca, pecaOriginal, bordas, transformacao, alertas, codigoPeca]);

// Recuperar backup ao carregar
useEffect(() => {
  const backup = localStorage.getItem('corewood_editor_backup');
  if (backup && !pecaInicial) {
    try {
      const dados = JSON.parse(backup);
      const horasAtras = (Date.now() - dados.timestamp) / (1000 * 60 * 60);
      
      if (horasAtras < 24 && dados.peca?.furos?.length > 0) {
        setBackupRecuperavel(dados);
      }
    } catch (e) {
      console.error('Erro ao recuperar backup:', e);
      localStorage.removeItem('corewood_editor_backup');
    }
  }
}, [pecaInicial]);

// Aplica o rascunho recuperado (confirmação do dialog)
const recuperarBackup = () => {
  const dados = backupRecuperavel;
  if (!dados) return;
  setPeca(dados.peca);
  setPecaOriginal(dados.pecaOriginal || null);
  setBordas(dados.bordas || { topo: 'nenhum', baixo: 'nenhum', esquerda: 'nenhum', direita: 'nenhum' });
  setTransformacao(dados.transformacao || { rotacao: 0, espelhado: false });
  setAlertas(dados.alertas || {});
  setCodigoPeca(dados.codigoPeca || '');
  setBackupRecuperavel(null);
};

// Descarta o rascunho salvo (botão "Descartar")
const descartarBackup = () => {
  localStorage.removeItem('corewood_editor_backup');
  setBackupRecuperavel(null);
};

  // ========================================
  // CARREGAR PEÇA INICIAL
  // ========================================
  useEffect(() => {
    if (pecaInicial) {
      const furosVerticais = pecaInicial.furos?.verticais || [];
      const furosHorizontais = pecaInicial.furos?.horizontais || [];
      
      const comprimento = parseFloat(pecaInicial.comprimento) || 0;
      const largura = parseFloat(pecaInicial.largura) || 0;
      const espessura = parseFloat(pecaInicial.espessura) || 15;

      // Furos do banco são ORIGINAIS (sem transformação aplicada)
      const pecaOrig = {
        comprimento,
        largura,
        furos: furosVerticais,
        furosHorizontais: furosHorizontais,
        rasgos: pecaInicial.rasgos || []
      };

      const savedTransf = {
        rotacao: pecaInicial.transformacao?.rotacao || 0,
        espelhado: pecaInicial.transformacao?.espelhado || false,
        invertido: pecaInicial.transformacao?.invertido || false
      };

      // Reaplicar a transformação salva para exibir corretamente
      const resultado = computarTransformacaoPura(pecaOrig, savedTransf);

      setPeca(prevPeca => ({
        ...prevPeca,
        nome: pecaInicial.nome || '',
        espessura,
        comprimento: resultado.comprimento,
        largura: resultado.largura,
        furos: resultado.furos,
        furosHorizontais: resultado.furosHorizontais,
        rasgos: resultado.rasgos
      }));

      setPecaOriginal(pecaOrig);
      setTransformacao(savedTransf);

      setCodigoPeca(pecaInicial.codigo || '');
      setAlertas(pecaInicial.alertas || {});
      setVersao(pecaInicial.versao || '00');
      setRegrasEspeciais(pecaInicial.regras_especiais || []);

      if (pecaInicial.bordas) {
        setBordas({
          topo: pecaInicial.bordas.topo || 'nenhum',
          baixo: pecaInicial.bordas.baixo || 'nenhum',
          esquerda: pecaInicial.bordas.esquerda || 'nenhum',
          direita: pecaInicial.bordas.direita || 'nenhum'
        });
      }

      const temFuros = furosVerticais.length > 0 || furosHorizontais.length > 0;
      const temRasgos = (pecaInicial.rasgos || []).length > 0;
      setDocsFuracao(temFuros);
      setDocsUsinagem(temRasgos);
    }
  }, [pecaInicial]);

  // Sincroniza checkboxes com o conteúdo real da peça
  useEffect(() => {
    const temFuros = peca.furos.length > 0 || (peca.furosHorizontais || []).length > 0;
    const temRasgos = (peca.rasgos || []).length > 0;
    if (!temFuros) setDocsFuracao(false);
    if (temRasgos) setDocsUsinagem(true);
  }, [peca.furos, peca.furosHorizontais, peca.rasgos]);

  // ========================================
  // HANDLERS DE DIMENSÕES
  // ========================================
  const handleDimensaoChange = (campo, valor) => {
    setPeca({
      ...peca,
      [campo]: parseFloat(valor) || 0
    });
  };

  // ========================================
  // HANDLERS DE FUROS
  // ========================================
  const handleAddFuro = (novoFuro) => {
    const uid = novoFuro.uid || _uid();
    const furoComId = { ...novoFuro, uid, id: uid };

    if (novoFuro.tipo === 'horizontal') {
      const novosFuros = [...(peca.furosHorizontais || []), furoComId];
      setPeca({ ...peca, furosHorizontais: novosFuros });

      if (pecaOriginal) {
        const furoOrig = inverterFuro(furoComId, 'horizontal', transformacao, pecaOriginal);
        setPecaOriginal(prev => ({
          ...prev,
          furosHorizontais: [...(prev.furosHorizontais || []), furoOrig],
        }));
      } else {
        // Primeiro item adicionado é horizontal — cria pecaOriginal agora
        setPecaOriginal({
          comprimento: peca.comprimento,
          largura: peca.largura,
          furos: peca.furos || [],
          furosHorizontais: novosFuros,
          rasgos: peca.rasgos || [],
        });
      }
    } else {
      const novosFuros = [...peca.furos, furoComId];
      setPeca({ ...peca, furos: novosFuros });

      if (!pecaOriginal) {
        // Primeira vez: peca ainda não foi transformada (peca === frame original)
        setPecaOriginal({
          comprimento: peca.comprimento,
          largura: peca.largura,
          furos: novosFuros,
          furosHorizontais: peca.furosHorizontais || [],
          rasgos: peca.rasgos || [],
        });
      } else {
        const furoOrig = inverterFuro(furoComId, 'vertical', transformacao, pecaOriginal);
        setPecaOriginal(prev => ({
          ...prev,
          furos: [...prev.furos, furoOrig],
        }));
      }
    }

    // Selecionar o furo recém-adicionado
    setSelectedFuro({
      tipo: novoFuro.tipo || 'vertical',
      index: novoFuro.tipo === 'horizontal'
        ? (peca.furosHorizontais?.length || 0)
        : peca.furos.length,
      uid: furoComId.uid,
      furo: furoComId,
    });
  };

  const handleSelectFuro = useCallback((furoData) => {
    setSelectedFuro(furoData);
  }, []);

  const handleDeleteFuro = useCallback((tipo, index) => {
    if (tipo === 'vertical') {
      const uid = peca.furos[index]?.uid;
      setPeca(prev => ({ ...prev, furos: prev.furos.filter((_, i) => i !== index) }));
      if (uid) setPecaOriginal(prev => prev ? { ...prev, furos: prev.furos.filter(f => f.uid !== uid) } : prev);
    } else {
      const uid = peca.furosHorizontais[index]?.uid;
      setPeca(prev => ({ ...prev, furosHorizontais: prev.furosHorizontais.filter((_, i) => i !== index) }));
      if (uid) setPecaOriginal(prev => prev ? { ...prev, furosHorizontais: (prev.furosHorizontais || []).filter(f => f.uid !== uid) } : prev);
    }
    setSelectedFuro(null);
  }, [peca]);

  const handleDeleteRasgo = useCallback((index) => {
    setPeca(prev => ({
      ...prev,
      rasgos: prev.rasgos.filter((_, i) => i !== index)
    }));
    setPecaOriginal(prev => prev ? { ...prev, rasgos: (prev.rasgos || []).filter((_, i) => i !== index) } : prev);
    setRasgoEditandoIndex(prev => prev === index ? null : prev);
  }, []);

  const handleSaveRasgo = useCallback((rasgo) => {
    setPeca(prev => {
      const novosRasgos = [...(prev.rasgos || [])];
      if (rasgoEditandoIndex !== null) {
        novosRasgos[rasgoEditandoIndex] = rasgo;
      } else {
        novosRasgos.push(rasgo);
      }
      return { ...prev, rasgos: novosRasgos };
    });
    setPecaOriginal(prev => {
      if (!prev) return prev;
      const novosRasgos = [...(prev.rasgos || [])];
      const rasgoOrig = inverterRasgo(rasgo, transformacao, prev);
      if (rasgoEditandoIndex !== null) {
        novosRasgos[rasgoEditandoIndex] = rasgoOrig;
      } else {
        novosRasgos.push(rasgoOrig);
      }
      return { ...prev, rasgos: novosRasgos };
    });
    setRasgoEditandoIndex(null);
  }, [rasgoEditandoIndex, transformacao]);

  const handleRasgoFieldChange = useCallback((rasgoConvertido) => {
    if (rasgoEditandoIndex === null) return;
    setPeca(prev => {
      const novosRasgos = [...(prev.rasgos || [])];
      novosRasgos[rasgoEditandoIndex] = { ...novosRasgos[rasgoEditandoIndex], ...rasgoConvertido };
      return { ...prev, rasgos: novosRasgos };
    });
  }, [rasgoEditandoIndex]);

  const toggleFuroSelecionado = useCallback((uid) => {
    setSelectedFuros((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  }, []);

  const handleRegrasAtualizadas = useCallback((novasRegras) => {
    setRegrasEspeciais(novasRegras);
    setSelectedFuros(new Set());
  }, []);

  const handleUpdateFuro = (campo, valor) => {
    if (!selectedFuro) return;

    const novoValor = campo === 'x' && valor === 'x' ? 'x' : (parseFloat(valor) || valor);
    const { tipo, index } = selectedFuro;

    if (tipo === 'horizontal') {
      const novosFuros = [...(peca.furosHorizontais || [])];
      novosFuros[index] = { ...novosFuros[index], [campo]: novoValor };
      setPeca({ ...peca, furosHorizontais: novosFuros });
      setSelectedFuro({ ...selectedFuro, furo: { ...selectedFuro.furo, [campo]: novoValor } });

      // Sincroniza pecaOriginal por (tipo, index) — não por uid, que pode faltar ou
      // não chegar dependendo de como o furo foi selecionado (lista vs. Canvas3D)
      if (pecaOriginal) {
        setPecaOriginal(sincronizarFuroOriginal(
          pecaOriginal, 'horizontal', index, campo, novoValor, transformacao, novosFuros[index]
        ));
      }
    } else {
      const novosFuros = [...peca.furos];
      novosFuros[index] = { ...novosFuros[index], [campo]: novoValor };
      setPeca({ ...peca, furos: novosFuros });
      setSelectedFuro({ ...selectedFuro, furo: { ...selectedFuro.furo, [campo]: novoValor } });

      // Sincroniza pecaOriginal por (tipo, index) — não por uid, que pode faltar ou
      // não chegar dependendo de como o furo foi selecionado (lista vs. Canvas3D)
      if (pecaOriginal) {
        setPecaOriginal(sincronizarFuroOriginal(
          pecaOriginal, 'vertical', index, campo, novoValor, transformacao, novosFuros[index]
        ));
      }
    }
  };

  // Replicar furo
  const handleReplicarFuro = (qtd, dist, dir) => {
    if (!selectedFuro || !qtd || !dist) {
      alert('Selecione um furo e preencha quantidade e distância!');
      return;
    }

    const novosFuros = [];
    for (let i = 1; i < qtd; i++) {
      const uid = _uid();
      novosFuros.push({
        ...selectedFuro.furo,
        id: uid,
        uid,
        x: selectedFuro.furo.x === 'x' ? 'x' : selectedFuro.furo.x + (dir === 'x' ? dist * i : 0),
        y: selectedFuro.furo.y + (dir === 'y' ? dist * i : 0)
      });
    }

    if (selectedFuro.tipo === 'horizontal') {
      setPeca({ ...peca, furosHorizontais: [...(peca.furosHorizontais || []), ...novosFuros] });
      if (pecaOriginal) {
        const origCopias = novosFuros.map(f => inverterFuro(f, 'horizontal', transformacao, pecaOriginal));
        setPecaOriginal(prev => ({ ...prev, furosHorizontais: [...(prev.furosHorizontais || []), ...origCopias] }));
      }
    } else {
      setPeca({ ...peca, furos: [...peca.furos, ...novosFuros] });
      if (pecaOriginal) {
        const origCopias = novosFuros.map(f => inverterFuro(f, 'vertical', transformacao, pecaOriginal));
        setPecaOriginal(prev => ({ ...prev, furos: [...prev.furos, ...origCopias] }));
      }
    }

    alert(`${qtd - 1} furos replicados!`);
  };

  // ========================================
  // TRANSFORMAÇÕES
  // ========================================
  const aplicarTransformacao = (novaTransformacao) => {
    let orig = pecaOriginal;
    if (!orig) {
      // pecaOriginal nulo: reconstruir invertendo a transformação atual sobre peca
      const rot = transformacao.rotacao || 0;
      const comprimentoOrig = (rot === 90 || rot === 270) ? peca.largura : peca.comprimento;
      const larguraOrig    = (rot === 90 || rot === 270) ? peca.comprimento : peca.largura;
      const fakeOrig = { comprimento: comprimentoOrig, largura: larguraOrig };
      const furosOrig = (peca.furos || []).map(f => inverterFuro(f, 'vertical', transformacao, fakeOrig));
      const furosHorizOrig = (peca.furosHorizontais || []).map(f => inverterFuro(f, 'horizontal', transformacao, fakeOrig));
      const rasgosOrig = (peca.rasgos || []).map(r => inverterRasgo(r, transformacao, fakeOrig));
      orig = { comprimento: comprimentoOrig, largura: larguraOrig, furos: furosOrig, furosHorizontais: furosHorizOrig, rasgos: rasgosOrig };
      setPecaOriginal(orig);
    }
    const resultado = computarTransformacaoPura(orig, novaTransformacao);
    setPeca(prev => ({ ...prev, ...resultado }));
    setTransformacao(novaTransformacao);
  };

  // ========================================
  // POSICIONAMENTO AUTOMÁTICO
  // ========================================
  const handleRecomendado = async () => {
    if (!pecaInicial?.id) return;
    const orig = pecaOriginal ?? {
      comprimento: peca.comprimento,
      largura: peca.largura,
      furos: peca.furos,
      furosHorizontais: peca.furosHorizontais,
    };
    setLoadingRecomendado(true);
    setErroRecomendado(null);
    setPlanoRecomendado(null);
    setNotaManual(false);
    try {
      await upsertBordas(pecaInicial.id, bordas);
      const resultado = await sugerirPosicionamento(pecaInicial.id, orig, peca.espessura, bordaBatenteForcada);
      const plano = resultado.planos[0];
      setPlanoRecomendado(plano);
      setPassadaIdx(0);
      const p = plano.passadas[0];
      // rotacao_graus do engine já é correto para todos os casos (fix jul/2026): com
      // horizontais, a família do eixo (XM/XP ou YM/YP) restringe a rotação a duas opções
      // giradas 180° entre si e borda_batente decide qual — baixo=90°/topo=270°,
      // esquerda=0°/direita=180° (docs/dominios/recomendador-regras.md RN-07). Vertical-only segue
      // rotacao_graus_para_borda direto. Vale igual se a borda foi forçada ou auto-escolhida.
      // não inverter LSU — manter vista de cima com marcação de face (vermelho + ponto), alinhado ao pdf_generator
      aplicarTransformacao({
        rotacao: p.pose.rotacao_graus,
        espelhado: transformacao.espelhado,
        invertido: false,
      });
      try {
        await aceitarPosicionamento(pecaInicial.id, orig, peca.espessura, bordaBatenteForcada, plano, p.pose.rotacao_graus);
      } catch (err) {
        console.error('aceitarPosicionamento:', err);
      }
    } catch (err) {
      const detalhe = err.response?.data?.detail;
      // Falha do Recomendado não pode deixar o canvas exibindo a rotação de uma tentativa
      // anterior (ex: do botão manual ↻) como se fosse o resultado desta chamada — reseta
      // os mesmos estados que o caminho de sucesso toca (transformacao, plano, passada).
      aplicarTransformacao({ rotacao: 0, espelhado: transformacao.espelhado, invertido: false });
      setPlanoRecomendado(null);
      setPassadaIdx(0);
      setNotaManual(false);
      if (detalhe?.codigo === 'horizontal_inacessivel') {
        setErroRecomendado('A borda não alcança os furos horizontais na peça.');
      } else if (detalhe?.codigo === 'peca_sem_dimensoes') {
        setErroRecomendado('Peça sem dimensões válidas — designe o .STEP antes de posicionar.');
      } else {
        setErroRecomendado('Não foi possível sugerir posicionamento. Verifique as bordas e tente novamente.');
      }
      console.error('sugerirPosicionamento:', err);
    } finally {
      setLoadingRecomendado(false);
    }
  };

  const handleNavPassada = (delta) => {
    if (!planoRecomendado) return;
    const novo = passadaIdx + delta;
    if (novo < 0 || novo >= planoRecomendado.passadas.length) return;
    const p = planoRecomendado.passadas[novo];
    setPassadaIdx(novo);
    // rotacao_graus do engine já é correto para todos os casos — inclui a borda_batente
    // como parte do cálculo quando há horizontais (fix jul/2026, ver handleRecomendado acima).
    // não inverter LSU — manter vista de cima com marcação de face (vermelho + ponto), alinhado ao pdf_generator
    aplicarTransformacao({
      rotacao: p.pose.rotacao_graus,
      espelhado: transformacao.espelhado,
      invertido: false,
    });
  };

  // ========================================
  // IMPORTAR MPR
  // ========================================
  const handleImportarMPR = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      // Parse feito no backend (fonte única de parse de MPR do sistema —
      // docs/referencia/mapas/mpr-para-peca.md) em vez de regex local no browser.
      const dados = await parseMPR(file);

      const furosVerticais = (dados.furos?.verticais || []).map(f => ({
        id: f.uid,
        uid: f.uid,
        tipo: 'vertical',
        x: f.x,
        y: f.y,
        diametro: f.diametro,
        profundidade: f.profundidade,
        lado: f.lado,
      }));

      const furosHorizontais = (dados.furos?.horizontais || []).map(f => ({
        id: f.uid,
        uid: f.uid,
        tipo: 'horizontal',
        x: f.x,
        y: f.y,
        z: f.z,
        diametro: f.diametro,
        profundidade: f.profundidade,
        lado: f.lado,
      }));

      const novaPeca = {
        nome: dados.nome || file.name.replace('.mpr', '').replace('.MPR', ''),
        largura: dados.largura,
        comprimento: dados.comprimento,
        espessura: dados.espessura,
        furos: furosVerticais,
        furosHorizontais: furosHorizontais
      };

      // pecaOriginal (base das transformações) nunca tem rasgos — .mpr não
      // representa rasgos (limitação do dialeto HOMAG), o import não tem de
      // onde os recuperar.
      const novaPecaOriginal = {
        comprimento: dados.comprimento,
        largura: dados.largura,
        furos: furosVerticais,
        furosHorizontais: furosHorizontais,
        rasgos: []
      };

      const rasgosAtuais = (peca.rasgos || []).length;
      if (rasgosAtuais > 0) {
        // Import substitui a peça inteira (comportamento correto e
        // intencional — o arquivo é a nova verdade completa) e não tem como
        // preservar rasgos. Gate de confirmação explícita antes de aplicar
        // (achado nº5, docs/referencia/mapas/mpr-para-peca.md). Peça sem rasgos não tem
        // esse dado a perder, então importa direto (mesmo doc).
        setImportPendente({
          novaPeca,
          novaPecaOriginal,
          rasgosAtuais,
          nVerticais: furosVerticais.length,
          nHorizontais: furosHorizontais.length,
        });
      } else {
        aplicarImportMPR(novaPeca, novaPecaOriginal, furosVerticais.length, furosHorizontais.length);
      }

    } catch (error) {
      const mensagem = error.response?.data?.detail || error.message;
      console.error('Erro ao importar MPR:', error);
      alert('Erro ao importar MPR: ' + mensagem);
    }

    e.target.value = '';
  };

  const aplicarImportMPR = (novaPeca, novaPecaOriginal, nVerticais, nHorizontais) => {
    setPeca(novaPeca);
    setPecaOriginal(novaPecaOriginal);
    setSelectedFuro(null);
    alert(`MPR importado!\n${nVerticais} furos verticais\n${nHorizontais} furos horizontais`);
  };

  const confirmarImportMPR = () => {
    if (!importPendente) return;
    const { novaPeca, novaPecaOriginal, nVerticais, nHorizontais } = importPendente;
    setImportPendente(null);
    aplicarImportMPR(novaPeca, novaPecaOriginal, nVerticais, nHorizontais);
  };

  const cancelarImportMPR = () => setImportPendente(null);

  // ========================================
  // EXPORTAR MPR
  // ========================================
  const handleExportarMPR = async () => {
    if (!peca.nome || !peca.largura || !peca.comprimento) {
      alert('Preencha nome e dimensões da peça!');
      return;
    }

    if (peca.furos.length === 0 && (!peca.furosHorizontais || peca.furosHorizontais.length === 0)) {
      alert('Adicione pelo menos um furo!');
      return;
    }

    try {
      // Exportar sempre a partir do frame original (sem transformação aplicada)
      const orig = pecaOriginal || peca;
      const pecaExportar = {
        nome: peca.nome,
        comprimento: orig.comprimento,
        largura: orig.largura,
        espessura: peca.espessura,
        furos: orig.furos || [],
        furosHorizontais: orig.furosHorizontais || [],
        comentarios: []
      };

      const mprBlob = await exportarMPR(pecaExportar);

      const url = window.URL.createObjectURL(mprBlob);
      const link = document.createElement('a');
      link.href = url;
      const nomeArquivo = codigoPeca || peca.nome || 'peca';
      link.download = `${nomeArquivo}.mpr`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert('MPR exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar MPR:', error);
      alert(`Erro ao exportar MPR:\n${error.response?.data?.detail || error.message}`);
    }
  };


  // ========================================
  // AGRUPAR / DESAGRUPAR FUROS
  // ========================================
  const handleAgruparFuros = async () => {
    if (peca.furos.length === 0 && (!peca.furosHorizontais || peca.furosHorizontais.length === 0)) return;
    try {
      const resultado = await agruparFuros(peca.furos, peca.furosHorizontais || []);
      setPeca(prev => ({
        ...prev,
        furos: resultado.furos_verticais,
        furosHorizontais: resultado.furos_horizontais,
      }));
    } catch (error) {
      console.error('Erro ao agrupar furos:', error);
      alert('Erro ao agrupar furos');
    }
  };

  const expandirFuros = (furos) => {
    const expandidos = [];
    furos.forEach(furo => {
      const qtd = furo.quantidade || 1;
      if (qtd <= 1) {
        expandidos.push({ ...furo, quantidade: 1, distancia: 0, direcao_replicacao: 'x' });
      } else {
        for (let n = 0; n < qtd; n++) {
          const uid = (furo.uids_replicados?.[n]) || _uid();
          expandidos.push({
            ...furo,
            id: uid,
            uid,
            x: furo.direcao_replicacao === 'x' ? furo.x + n * furo.distancia : furo.x,
            y: furo.direcao_replicacao === 'y' ? furo.y - n * furo.distancia : furo.y,
            quantidade: 1,
            distancia: 0,
            direcao_replicacao: 'x',
          });
        }
      }
    });
    return expandidos;
  };

  const handleDesagruparFuros = () => {
    setPeca(prev => ({
      ...prev,
      furos: expandirFuros(prev.furos),
      furosHorizontais: expandirFuros(prev.furosHorizontais || []),
    }));
  };

  const temGrupos = peca.furos.some(f => (f.quantidade || 1) > 1)
    || (peca.furosHorizontais || []).some(f => (f.quantidade || 1) > 1);
  const temIndividuais = peca.furos.some(f => (f.quantidade || 1) <= 1)
    || (peca.furosHorizontais || []).some(f => (f.quantidade || 1) <= 1);

  // ========================================
  // GERAR DOCUMENTOS (FURAÇÃO + USINAGEM)
  // ========================================
  const handleGerarDocs = async () => {
    if (!peca.nome || !peca.largura || !peca.comprimento) {
      alert('Preencha nome e dimensões da peça!');
      return;
    }
    if (!docsFuracao && !docsUsinagem) {
      alert('Selecione pelo menos um tipo de documento!');
      return;
    }

    // Se vai gerar furação, buscar páginas para configurar alertas
    if (docsFuracao) {
      const temFuros = peca.furos.length > 0 || (peca.furosHorizontais || []).length > 0;
      if (temFuros) {
        try {
          setLoadingPreview(true);
          const resultado = await previewPaginas(peca);
          setDraftAlertas({ ...alertas });
          setModalAlertas(resultado.paginas);
        } catch (err) {
          console.error('Erro ao buscar páginas:', err);
          await _executarGeracao({});
        } finally {
          setLoadingPreview(false);
        }
        return; // aguarda confirmação do modal
      }
    }

    await _executarGeracao(alertas);
  };

  const _executarGeracao = async (alertasConfirmados) => {
    const pecaComId = {
      ...peca,
      peca_id: pecaInicial?.id || null,
      bordas,
      transformacao,
      alertas: alertasConfirmados,
      versao
    };

    let erros = [];
    let avisos = [];

    if (docsFuracao) {
      const temFuros = peca.furos.length > 0 || (peca.furosHorizontais || []).length > 0;
      if (!temFuros) {
        erros.push('Furação: nenhum furo encontrado');
      } else {
        try {
          const { blob: pdfBlob, total: totalAvisos } = await gerarPDFEditor(pecaComId);
          const url  = window.URL.createObjectURL(pdfBlob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${peca.nome}_furacao.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          if (totalAvisos > 0) {
            avisos.push(
              `Furação: ${totalAvisos} furo(s) não pôde(puderam) ser transformado(s) ` +
              `(rotação/espelhamento) e ficou(ram) na posição original — confira o desenho.`
            );
          }
        } catch (err) {
          erros.push(`Furação: ${err.response?.data?.detail || err.message}`);
        }
      }
    }

    if (docsUsinagem) {
      const temRasgos = (peca.rasgos || []).length > 0;
      if (!temRasgos) {
        erros.push('Usinagem: nenhum rasgo encontrado');
      } else {
        try {
          const pdfBlob = await gerarPDFUsinagem(pecaComId);
          const url  = window.URL.createObjectURL(pdfBlob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${peca.nome}_usinagem.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (err) {
          erros.push(`Usinagem: ${err.response?.data?.detail || err.message}`);
        }
      }
    }

    const blocoAvisos = avisos.length > 0 ? `\n\nAvisos:\n${avisos.join('\n')}` : '';
    if (erros.length > 0) {
      alert(`Erros ao gerar documentos:\n${erros.join('\n')}${blocoAvisos}`);
    } else if (avisos.length > 0) {
      alert(`Documento(s) gerado(s) com avisos:\n${avisos.join('\n')}`);
    } else {
      alert('Documento(s) gerado(s) com sucesso!');
    }
  };

  // ========================================
  // SALVAR PEÇA
  // ========================================
  const handleSalvarPeca = async () => {
    if (!pecaInicial?.id) {
      alert('Peça não identificada para salvar');
      return;
    }
    setLoading(true);

    try {
      // Quando não há transformação ativa, peca já é o estado correto.
      // Quando há transformação, usamos pecaOriginal (coordenadas pré-transform) para
      // que o servidor possa reaplicar a transformação ao carregar.
      const hasTransform = transformacao.rotacao !== 0 || transformacao.espelhado || transformacao.invertido;
      const orig = hasTransform
        ? (pecaOriginal || {
            comprimento: peca.comprimento,
            largura: peca.largura,
            furos: peca.furos,
            furosHorizontais: peca.furosHorizontais,
            rasgos: peca.rasgos
          })
        : {
            comprimento: peca.comprimento,
            largura: peca.largura,
            furos: peca.furos || [],
            furosHorizontais: peca.furosHorizontais || [],
            rasgos: peca.rasgos || []
          };

      // Aviso (não bloqueia): divergência de tamanho entre peca (tela) e o que será
      // enviado pode ser dessincronização por bug de edição OU efeito legítimo de
      // Agrupar/Desagrupar Furos (que hoje não atualiza pecaOriginal) — não dá para
      // distinguir os dois com segurança aqui, então só avisamos no console.
      if (hasTransform) {
        if ((peca.furos || []).length !== (orig.furos || []).length) {
          console.warn(
            `handleSalvarPeca: peca.furos tem ${peca.furos.length} itens, mas o payload ` +
            `(pecaOriginal) tem ${(orig.furos || []).length} — possível dessincronização ` +
            `(edição perdida ou Agrupar/Desagrupar Furos).`
          );
        }
        if ((peca.furosHorizontais || []).length !== (orig.furosHorizontais || []).length) {
          console.warn(
            `handleSalvarPeca: peca.furosHorizontais tem ${(peca.furosHorizontais || []).length} itens, ` +
            `mas o payload (pecaOriginal) tem ${(orig.furosHorizontais || []).length} — possível ` +
            `dessincronização (edição perdida ou Agrupar/Desagrupar Furos).`
          );
        }
      }

      const formData = new FormData();
      formData.append('largura', orig.largura);
      formData.append('comprimento', orig.comprimento);
      formData.append('espessura', peca.espessura);
      formData.append('furos', JSON.stringify({
        verticais: orig.furos || [],
        horizontais: orig.furosHorizontais || []
      }));
      formData.append('rasgos', JSON.stringify(orig.rasgos || []));
      formData.append('bordas', JSON.stringify(bordas));
      formData.append('transformacao', JSON.stringify(transformacao));
      formData.append('alertas', JSON.stringify(alertas || {}));
      formData.append('versao', versao);

      const response = await api.put(
        `/pecas/${pecaInicial.id}/salvar`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.status === 200) {
        alert('Peça salva com sucesso!');
        localStorage.removeItem('corewood_editor_backup');
        if (onPecaAtualizada) {
          onPecaAtualizada(response.data);
        }
      } else {
        alert('Erro ao salvar peça');
      }
    } catch (error) {
      console.error(error);
      alert('Erro ao salvar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // NOVA PEÇA
  // ========================================
  const handleNovaPeca = async () => {
    if (peca.furos.length > 0 || (peca.furosHorizontais && peca.furosHorizontais.length > 0)) {
      if (!await confirm({
        title: 'Começar nova peça',
        message: 'Descartar peça atual e começar nova?',
        confirmLabel: 'Descartar e começar',
        confirmVariant: 'danger',
      })) {
        return;
      }
    }

    setPeca({
      nome: '',
      largura: 0,
      comprimento: 0,
      espessura: 15,
      furos: [],
      furosHorizontais: []
    });
    setBordas({
      topo: 'nenhum',
      baixo: 'nenhum',
      esquerda: 'nenhum',
      direita: 'nenhum'
    });
    setCodigoPeca('');
    setAlertas({});
    setSelectedFuro(null);
    setTransformacao({ rotacao: 0, espelhado: false, invertido: false });
    setPecaOriginal(null);
  };

  // ========================================
  // CTRL+C / CTRL+V — Copiar e colar operações
  // ========================================
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Não interceptar quando o foco está em inputs
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;

      if (e.ctrlKey && e.key === 'c') {
        if (selectedFuro) {
          setClipboard({ tipoOp: 'furo', tipo: selectedFuro.tipo, furo: { ...selectedFuro.furo } });
        } else if (rasgoEditandoIndex !== null) {
          const rasgo = peca.rasgos?.[rasgoEditandoIndex];
          if (rasgo) setClipboard({ tipoOp: 'rasgo', rasgo: { ...rasgo } });
        }
      }

      if (e.ctrlKey && e.key === 'v') {
        if (!clipboard) return;
        e.preventDefault();

        if (clipboard.tipoOp === 'furo') {
          const novoUid = _uid();
          const novoFuro = {
            ...clipboard.furo,
            id: novoUid,
            uid: novoUid,
            x: typeof clipboard.furo.x === 'number' ? clipboard.furo.x + 10 : clipboard.furo.x,
            y: clipboard.furo.y + 10,
          };
          if (clipboard.tipo === 'horizontal') {
            const novoIndex = (peca.furosHorizontais || []).length;
            setPeca(prev => ({ ...prev, furosHorizontais: [...(prev.furosHorizontais || []), novoFuro] }));
            if (pecaOriginal) {
              const furoOrig = inverterFuro(novoFuro, 'horizontal', transformacao, pecaOriginal);
              setPecaOriginal(prev => ({ ...prev, furosHorizontais: [...(prev.furosHorizontais || []), furoOrig] }));
            }
            setSelectedFuro({ tipo: 'horizontal', index: novoIndex, uid: novoUid, furo: novoFuro });
          } else {
            const novoIndex = peca.furos.length;
            setPeca(prev => ({ ...prev, furos: [...prev.furos, novoFuro] }));
            if (pecaOriginal) {
              const furoOrig = inverterFuro(novoFuro, 'vertical', transformacao, pecaOriginal);
              setPecaOriginal(prev => ({ ...prev, furos: [...prev.furos, furoOrig] }));
            }
            setSelectedFuro({ tipo: 'vertical', index: novoIndex, uid: novoUid, furo: novoFuro });
          }
        } else if (clipboard.tipoOp === 'rasgo') {
          const novoRasgo = { ...clipboard.rasgo, x: clipboard.rasgo.x + 10, y: clipboard.rasgo.y + 10 };
          setPeca(prev => ({ ...prev, rasgos: [...(prev.rasgos || []), novoRasgo] }));
          if (pecaOriginal) {
            const rasgoOrig = inverterRasgo(novoRasgo, transformacao, pecaOriginal);
            setPecaOriginal(prev => prev ? { ...prev, rasgos: [...(prev.rasgos || []), rasgoOrig] } : prev);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedFuro, rasgoEditandoIndex, clipboard, peca, pecaOriginal, transformacao]);

  // ========================================
  // RESIZE HANDLERS
  // ========================================
  const handleSidebarResize = useCallback((e) => {
    const startX = e.clientX;
    const startWidth = sidebarWidth;

    const onMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      const newWidth = Math.max(200, Math.min(400, startWidth + delta));
      setSidebarWidth(newWidth);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [sidebarWidth]);

  const handlePanelResize = useCallback((e) => {
    const startY = e.clientY;
    const startHeight = panelHeight;

    const onMouseMove = (moveEvent) => {
      const delta = startY - moveEvent.clientY;
      const newHeight = Math.max(200, Math.min(500, startHeight + delta));
      setPanelHeight(newHeight);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [panelHeight]);

  // ========================================
  // RENDER
  // ========================================
  // ========================================
  // MODAL DE ALERTAS
  // ========================================
  const handleConfirmarAlertas = async () => {
    setAlertas(draftAlertas);
    setModalAlertas(null);
    await _executarGeracao(draftAlertas);
  };

  const renderModalAlertas = () => {
    if (!modalAlertas) return null;
    return (
      <div className="modal-overlay" onClick={() => setModalAlertas(null)}>
        <div className="modal-alertas" onClick={e => e.stopPropagation()}>
          <h3>Alertas por página</h3>
          <p className="modal-alertas-hint">
            Preencha o texto para exibir o triângulo de atenção em cada página. Deixe em branco para não exibir.
          </p>
          <div className="modal-alertas-lista">
            {modalAlertas.map(({ titulo }) => (
              <div key={titulo} className="modal-alerta-item">
                <label className="modal-alerta-label">{titulo}</label>
                <textarea
                  className="modal-alerta-textarea"
                  value={draftAlertas[titulo] || ''}
                  onChange={e => setDraftAlertas(d => ({ ...d, [titulo]: e.target.value }))}
                  rows={2}
                  placeholder="Sem alerta"
                />
              </div>
            ))}
          </div>
          <div className="modal-alertas-actions">
            <button className="btn-secundario" onClick={() => setModalAlertas(null)}>
              Cancelar
            </button>
            <button className="btn-primario" onClick={handleConfirmarAlertas}>
              Gerar PDF
            </button>
          </div>
        </div>
      </div>
    );
  };

  const _vtc = _VISUAL_TO_CANONICAL_BORDA[transformacao.rotacao] || _VISUAL_TO_CANONICAL_BORDA[0];
  const bordasVisuais = {
    topo:     bordas[_vtc.topo],
    baixo:    bordas[_vtc.baixo],
    esquerda: bordas[_vtc.esquerda],
    direita:  bordas[_vtc.direita],
  };

  return (
    <div className="editor-mpr-container">
      {renderModalAlertas()}
      {confirmEl}
      <ConfirmDialog
        open={backupRecuperavel !== null}
        title="Peça não salva encontrada"
        message={backupRecuperavel ? `Encontramos uma peça não salva (${backupRecuperavel.peca?.nome || 'sem nome'}).\n\nDeseja recuperá-la para continuar a edição?` : ''}
        confirmLabel="Recuperar"
        cancelLabel="Descartar"
        confirmVariant="primary"
        onConfirm={recuperarBackup}
        onCancel={descartarBackup}
        onClose={() => setBackupRecuperavel(null)}
      />
      {importPendente && (
        <Modal onClose={cancelarImportMPR}>
          <h3 className="cw-section-title">Importar MPR vai substituir a peça inteira</h3>
          <p className="import-mpr-confirmacao-texto">
            Importar este arquivo troca a peça inteira pelo conteúdo do .mpr — furos,
            dimensões e nome atuais serão descartados e substituídos pelos do arquivo.
          </p>
          <p className="import-mpr-confirmacao-texto">
            Os <strong>{importPendente.rasgosAtuais}</strong>{' '}
            {importPendente.rasgosAtuais === 1 ? 'rasgo atual será removido' : 'rasgos atuais serão removidos'}:
            arquivos .mpr não têm como representar rasgos (limitação do dialeto HOMAG), então eles
            não voltam depois de importar.
          </p>
          <div className="import-mpr-confirmacao-acoes">
            <Button variant="secondary" onClick={cancelarImportMPR}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmarImportMPR}>
              Substituir
            </Button>
          </div>
        </Modal>
      )}
      {/* ==================== HEADER ==================== */}
      <header className="editor-header">
        <div className="header-left">
          {onVoltar && (
            <button className="btn-back" onClick={onVoltar}>
              ← Voltar
            </button>
          )}
          <div className="header-title">
            <input
              type="text"
              className="input-code"
              placeholder="Código"
              value={codigoPeca}
              onChange={(e) => setCodigoPeca(e.target.value)}
            />
            <input
              type="text"
              className="input-title"
              placeholder="Nome da peça"
              value={peca.nome}
              onChange={(e) => setPeca({ ...peca, nome: e.target.value })}
            />
          </div>
        </div>
        <div className="header-actions">
          <input
            type="file"
            accept=".mpr,.MPR"
            onChange={handleImportarMPR}
            style={{ display: 'none' }}
            id="import-mpr"
          />
          <label htmlFor="import-mpr" className="btn btn-secondary">
            Importar MPR
          </label>
          <button className="btn btn-secondary" onClick={handleNovaPeca}>
            Nova
          </button>
          {pecaInicial && (
            <button
              className="btn btn-secondary"
              onClick={handleSalvarPeca}
              disabled={loading}
            >
              {loading ? '...' : 'Salvar'}
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={handleAgruparFuros}
            disabled={peca.furos.length === 0 || !temIndividuais}
            title="Agrupa furos no mesmo eixo com passo constante (AN/AB/WI no MPR)"
          >
            ⊞ Agrupar
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleDesagruparFuros}
            disabled={!temGrupos}
            title="Expande grupos de volta para furos individuais"
          >
            ⊟ Desagrupar
          </button>
          <button
            className="btn btn-warning"
            onClick={handleExportarMPR}
            disabled={!peca.nome || (peca.furos.length === 0 && (!peca.furosHorizontais || peca.furosHorizontais.length === 0))}
          >
            Exportar MPR
          </button>
          <div className="gerar-docs-group">
            <label className="doc-checkbox" title="Documento de furação">
              <input
                type="checkbox"
                checked={docsFuracao}
                onChange={e => setDocsFuracao(e.target.checked)}
                disabled={peca.furos.length === 0 && (!peca.furosHorizontais || peca.furosHorizontais.length === 0)}
              />
              Furação
            </label>
            <label className="doc-checkbox" title="Documento de usinagem (rasgos)">
              <input
                type="checkbox"
                checked={docsUsinagem}
                onChange={e => setDocsUsinagem(e.target.checked)}
                disabled={!peca.rasgos || peca.rasgos.length === 0}
              />
              Usinagem
            </label>
            <button
              className="btn btn-primary"
              onClick={handleGerarDocs}
              disabled={!peca.nome || (!docsFuracao && !docsUsinagem)}
            >
              Gerar
            </button>
          </div>
        </div>
      </header>

      {/* ==================== MAIN CONTENT ==================== */}
      <div className="editor-main">
        {/* Sidebar - Lista de Furos */}
        <aside className="editor-sidebar" style={{ width: sidebarWidth }}>
          <FurosList
            furosVerticais={peca.furos}
            furosHorizontais={peca.furosHorizontais}
            rasgos={peca.rasgos}
            selectedFuro={selectedFuro}
            onSelectFuro={handleSelectFuro}
            onDeleteFuro={handleDeleteFuro}
            onDeleteRasgo={handleDeleteRasgo}
            rasgoEditandoIndex={rasgoEditandoIndex}
            onSelectRasgo={setRasgoEditandoIndex}
            selectedFuros={selectedFuros}
            onToggleFuroSelecionado={pecaInicial?.id ? toggleFuroSelecionado : null}
          />
          {pecaInicial?.id && (
            <RegrasEspeciaisSection
              pecaId={pecaInicial.id}
              regrasEspeciais={regrasEspeciais}
              selectedFuros={selectedFuros}
              furosVerticais={peca.furos}
              onRegrasAtualizadas={handleRegrasAtualizadas}
              onHoverRegra={setHoverRegra}
            />
          )}
          <div className="resize-handle-h" onMouseDown={handleSidebarResize} />
        </aside>

        {/* Canvas 3D */}
        <div className="editor-canvas-wrapper">
          <Canvas3D
            peca={peca}
            bordas={bordasVisuais}
            transformacao={transformacao}
            selectedFuro={selectedFuro}
            onSelectFuro={handleSelectFuro}
            furosDestacados={hoverRegra ? new Set(hoverRegra.uids_furos) : new Set()}
          />
          <div className="canvas-legenda">
            <div className="legenda-item">
              <span className="legenda-dot" style={{ background: '#1c1c1c', border: '1px solid #666' }} />
              Furo Superior
            </div>
            <div className="legenda-item">
              <span className="legenda-dot" style={{ background: '#ff0000' }} />
              Furo Inferior
            </div>
            <div className="legenda-item">
              <span className="legenda-dot" style={{ background: '#448aff' }} />
              Furo Horizontal
            </div>
          </div>
        </div>
      </div>

      {/* Resize handle vertical */}
      <div className="resize-handle-v" onMouseDown={handlePanelResize} />

      {/* ==================== BOTTOM PANEL ==================== */}
      <div className="editor-panel" style={{ height: panelHeight }}>

        {/* Barra de abas */}
        <div className="panel-tabs">
          <button
            className={`panel-tab ${activeTab === 'config' ? 'active' : ''}`}
            onClick={() => setActiveTab('config')}
          >
            CONFIG
          </button>
          <button
            className={`panel-tab ${activeTab === 'furo' ? 'active' : ''}`}
            onClick={() => setActiveTab('furo')}
          >
            FURO
          </button>
          <button
            className={`panel-tab ${activeTab === 'rasgo' ? 'active' : ''}`}
            onClick={() => setActiveTab('rasgo')}
          >
            RASGO
          </button>
          {selectedFuro && (
            <button
              className={`panel-tab panel-tab-edit ${activeTab === 'edit' ? 'active' : ''}`}
              onClick={() => setActiveTab('edit')}
            >
              {selectedFuro.tipo === 'vertical' ? 'V' : 'H'} Furo {selectedFuro.tipo === 'vertical' ? 'V' : 'H'}{selectedFuro.index + 1}
            </button>
          )}
        </div>

        {/* Conteúdo da aba */}
        <div className="panel-tab-content">

          {/* ABA: Config */}
          {activeTab === 'config' && (
            <div className="tab-config-layout">
              {/* Dimensões */}
              <div className="tab-config-section">
                <span className="tab-section-label">Dimensões</span>
                <div className="form-grid">
                  <div className="form-field">
                    <label>X (Comp.)</label>
                    <div className="input-with-unit">
                      <input
                        type="number"
                        value={peca.comprimento || ''}
                        onChange={(e) => handleDimensaoChange('comprimento', e.target.value)}
                        placeholder="0"
                      />
                      <span>mm</span>
                    </div>
                  </div>
                  <div className="form-field">
                    <label>Y (Larg.)</label>
                    <div className="input-with-unit">
                      <input
                        type="number"
                        value={peca.largura || ''}
                        onChange={(e) => handleDimensaoChange('largura', e.target.value)}
                        placeholder="0"
                      />
                      <span>mm</span>
                    </div>
                  </div>
                  <div className="form-field">
                    <label>Z (Esp.)</label>
                    <div className="input-with-unit">
                      <input
                        type="number"
                        value={peca.espessura || ''}
                        onChange={(e) => handleDimensaoChange('espessura', e.target.value)}
                        placeholder="15"
                      />
                      <span>mm</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="tab-config-divider" />

              {/* Transformações */}
              <div className="tab-config-section">
                <span className="tab-section-label">Transformação</span>
                <div className="transform-row">
                  <button
                    className={`transform-btn-sm ${transformacao.rotacao !== 0 ? 'active' : ''}`}
                    onClick={() => {
                      if (planoRecomendado) { setPlanoRecomendado(null); setNotaManual(true); }
                      aplicarTransformacao({ ...transformacao, rotacao: (transformacao.rotacao + 90) % 360 });
                    }}
                  >
                    ↻ {transformacao.rotacao}°
                  </button>
                  <button
                    className={`transform-btn-sm ${transformacao.espelhado ? 'active' : ''}`}
                    onClick={() => {
                      if (planoRecomendado) { setPlanoRecomendado(null); setNotaManual(true); }
                      aplicarTransformacao({ ...transformacao, espelhado: !transformacao.espelhado });
                    }}
                  >
                    ⇄ Esp.
                  </button>
                  <button
                    className={`transform-btn-sm ${transformacao.invertido ? 'active-danger' : ''}`}
                    onClick={() => {
                      if (planoRecomendado) { setPlanoRecomendado(null); setNotaManual(true); }
                      aplicarTransformacao({ ...transformacao, invertido: !transformacao.invertido });
                    }}
                    title="Inverte faces de furação: superior ↔ inferior"
                  >
                    ⬍ Inv.
                  </button>
                </div>

                <button
                  className={`recomendado-btn${planoRecomendado ? ' ativo' : ''}`}
                  onClick={handleRecomendado}
                  disabled={!pecaInicial?.id || loadingRecomendado || (parseFloat(pecaInicial?.comprimento) || 0) < 1.0 || (parseFloat(pecaInicial?.largura) || 0) < 1.0}
                  title={
                    !pecaInicial?.id
                      ? 'Salve a peça antes de usar o posicionamento automático'
                      : (parseFloat(pecaInicial?.comprimento) || 0) < 1.0 || (parseFloat(pecaInicial?.largura) || 0) < 1.0
                        ? 'Peça aguardando designação do .STEP'
                        : 'Sugerir posicionamento'
                  }
                >
                  {loadingRecomendado ? 'Calculando...' : 'Recomendado'}
                </button>

                <div className="borda-batente-selector">
                  <span className="borda-batente-label">Borda Batente</span>
                  <div className="borda-batente-opcoes">
                    {[null, 'baixo', 'esquerda', 'direita', 'topo'].map(opcao => (
                      <button
                        key={opcao ?? 'auto'}
                        className={`borda-batente-opcao${bordaBatenteForcada === opcao ? ' ativo' : ''}`}
                        onClick={() => setBordaBatenteForcada(opcao)}
                      >
                        {opcao === null ? 'Auto' : opcao.charAt(0).toUpperCase() + opcao.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {notaManual && !planoRecomendado && (
                  <div className="nota-manual">
                    Orientação manual — Recomendado disponível novamente
                  </div>
                )}

                {planoRecomendado && (
                  <div className="recomendado-stepper">
                    <div className="stepper-nav">
                      <button
                        className="stepper-nav-btn"
                        onClick={() => handleNavPassada(-1)}
                        disabled={passadaIdx === 0}
                      >‹</button>
                      <span className="stepper-label">
                        P{passadaIdx + 1} / {planoRecomendado.passadas.length}
                      </span>
                      <button
                        className="stepper-nav-btn"
                        onClick={() => handleNavPassada(1)}
                        disabled={passadaIdx === planoRecomendado.passadas.length - 1}
                      >›</button>
                      <button
                        className="stepper-fechar"
                        onClick={() => setPlanoRecomendado(null)}
                        title="Sair do posicionamento recomendado"
                      >×</button>
                    </div>
                    <div className="stepper-info">
                      {(() => {
                        const p = planoRecomendado.passadas[passadaIdx];
                        const nOps = Object.values(p.operacoes_por_grupo).flat().length;
                        return `Face: ${p.pose.face_superior} · Bat: ${p.borda_batente} · ${nOps} op.`;
                      })()}
                    </div>
                  </div>
                )}

                {erroRecomendado && (
                  <div className="recomendado-erro">
                    <span>{erroRecomendado}</span>
                    <button onClick={() => setErroRecomendado(null)}>×</button>
                  </div>
                )}
              </div>

              <div className="tab-config-divider" />

              {/* Versão */}
              <div className="tab-config-section">
                <span className="tab-section-label">Versão</span>
                <input
                  type="number"
                  min="0"
                  max="99"
                  value={parseInt(versao, 10) || 0}
                  onChange={(e) => {
                    const num = Math.max(0, Math.min(99, parseInt(e.target.value, 10) || 0));
                    setVersao(String(num).padStart(2, '0'));
                  }}
                  className="versao-input"
                />
              </div>

              <div className="tab-config-divider" />

              {/* Bordas */}
              <div className="tab-config-section">
                <span className="tab-section-label">Bordas</span>
                <div className="bordas-retangulo">
                  <div className="borda-topo">
                    <select
                      value={bordasVisuais.topo}
                      onChange={(e) => setBordas({ ...bordas, [_vtc.topo]: e.target.value })}
                      className={`borda-select-sm ${bordasVisuais.topo}`}
                    >
                      <option value="nenhum">-</option>
                      <option value="cor">Cor</option>
                      <option value="pardo">Pardo</option>
                    </select>
                  </div>
                  <div className="borda-meio">
                    <select
                      value={bordasVisuais.esquerda}
                      onChange={(e) => setBordas({ ...bordas, [_vtc.esquerda]: e.target.value })}
                      className={`borda-select-sm ${bordasVisuais.esquerda}`}
                    >
                      <option value="nenhum">-</option>
                      <option value="cor">Cor</option>
                      <option value="pardo">Pardo</option>
                    </select>
                    <div className="borda-peca-icon">▭</div>
                    <select
                      value={bordasVisuais.direita}
                      onChange={(e) => setBordas({ ...bordas, [_vtc.direita]: e.target.value })}
                      className={`borda-select-sm ${bordasVisuais.direita}`}
                    >
                      <option value="nenhum">-</option>
                      <option value="cor">Cor</option>
                      <option value="pardo">Pardo</option>
                    </select>
                  </div>
                  <div className="borda-baixo">
                    <select
                      value={bordasVisuais.baixo}
                      onChange={(e) => setBordas({ ...bordas, [_vtc.baixo]: e.target.value })}
                      className={`borda-select-sm ${bordasVisuais.baixo}`}
                    >
                      <option value="nenhum">-</option>
                      <option value="cor">Cor</option>
                      <option value="pardo">Pardo</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="tab-config-divider" />

              {/* Alertas por página */}
              <div className="tab-config-section tab-config-section--obs">
                <span className="checkbox-label">Alertas</span>
                {Object.keys(alertas).filter(k => alertas[k]).length > 0 && (
                  <span className="alertas-badge">
                    {Object.keys(alertas).filter(k => alertas[k]).length} página(s)
                  </span>
                )}
                <button
                  className="btn-alertas-config"
                  onClick={async () => {
                    const temFuros = peca.furos.length > 0 || (peca.furosHorizontais || []).length > 0;
                    if (!temFuros) { alert('Adicione furos primeiro.'); return; }
                    try {
                      setLoadingPreview(true);
                      const resultado = await previewPaginas(peca);
                      setDraftAlertas({ ...alertas });
                      setModalAlertas(resultado.paginas);
                    } finally {
                      setLoadingPreview(false);
                    }
                  }}
                  disabled={loadingPreview}
                >
                  {loadingPreview ? '...' : 'Configurar'}
                </button>
              </div>
            </div>
          )}

          {/* ABA: Adicionar Furo */}
          {activeTab === 'furo' && (
            <div className="tab-content-inner">
              {peca.largura && peca.comprimento ? (
                <FuroManual
                  onAddFuro={handleAddFuro}
                  pecaDimensoes={{
                    comprimento: peca.comprimento,
                    largura: peca.largura,
                    espessura: peca.espessura
                  }}
                />
              ) : (
                <div className="placeholder">
                  <p>Defina as dimensões na aba Config primeiro</p>
                </div>
              )}
            </div>
          )}

          {/* ABA: Adicionar Rasgo */}
          {activeTab === 'rasgo' && (
            <div className="tab-content-inner">
              <RasgoManual
                onSaveRasgo={handleSaveRasgo}
                rasgoEditando={rasgoEditandoIndex !== null ? peca.rasgos?.[rasgoEditandoIndex] : null}
                onCancelEdit={() => setRasgoEditandoIndex(null)}
                rasgoKey={rasgoEditandoIndex}
                onFieldChange={handleRasgoFieldChange}
                pecaDimensoes={{
                  comprimento: peca.comprimento,
                  largura: peca.largura
                }}
              />
            </div>
          )}

          {/* ABA: Editar Furo Selecionado */}
          {activeTab === 'edit' && selectedFuro && (
            <div className="tab-content-inner tab-edit-content">
              <div className="furo-config">
                <div className="form-row-inline">
                  <div className="form-field-sm">
                    <label>X</label>
                    <input
                      type="text"
                      value={selectedFuro.furo.x}
                      onChange={(e) => handleUpdateFuro('x', e.target.value)}
                    />
                  </div>
                  <div className="form-field-sm">
                    <label>Y</label>
                    <input
                      type="number"
                      value={selectedFuro.furo.y}
                      onChange={(e) => handleUpdateFuro('y', e.target.value)}
                    />
                  </div>
                  {selectedFuro.tipo === 'horizontal' && (
                    <div className="form-field-sm">
                      <label>Z</label>
                      <input
                        type="number"
                        value={selectedFuro.furo.z || peca.espessura / 2}
                        onChange={(e) => handleUpdateFuro('z', e.target.value)}
                      />
                    </div>
                  )}
                  <div className="form-field-sm">
                    <label>Ø</label>
                    <input
                      type="number"
                      value={selectedFuro.furo.diametro}
                      onChange={(e) => handleUpdateFuro('diametro', e.target.value)}
                    />
                  </div>
                  <div className="form-field-sm">
                    <label>Prof.</label>
                    <input
                      type="number"
                      value={selectedFuro.furo.profundidade}
                      onChange={(e) => handleUpdateFuro('profundidade', e.target.value)}
                    />
                  </div>
                  {selectedFuro.tipo === 'horizontal' && (
                    <div className="form-field-sm">
                      <label>Lado</label>
                      <select
                        value={selectedFuro.furo.lado}
                        onChange={(e) => handleUpdateFuro('lado', e.target.value)}
                      >
                        {/* Rotulos pela BORDA DE ENTRADA da broca, regra oficial
                            [CNC] (bancada WoodWOP 6.0.18, 2026-07-24) -- ver
                            docs/referencia/formato-mpr/campo-bm-horizontal.md §9.1. Exibir os
                            valores crus foi o que deixou a convencao invertida
                            sobreviver despercebida no widget de FuroManual. */}
                        <option value="XP">XP — esquerda (X=0)</option>
                        <option value="XM">XM — direita (X=comprimento)</option>
                        <option value="YP">YP — superior (Y=0)</option>
                        <option value="YM">YM — inferior (Y=largura)</option>
                      </select>
                    </div>
                  )}
                  {selectedFuro.tipo === 'vertical' && (
                    <div className="form-field-sm">
                      <label>Lado</label>
                      <select
                        value={selectedFuro.furo.lado === 'LSU' ? 'LI' : (selectedFuro.furo.lado || 'LS')}
                        onChange={(e) => handleUpdateFuro('lado', e.target.value)}
                      >
                        <option value="LS">Superior</option>
                        <option value="LI">Inferior</option>
                      </select>
                    </div>
                  )}
                </div>

                {/* Replicação */}
                <div className="replicacao-row">
                  <input
                    type="number"
                    placeholder="Qtd"
                    min="2"
                    max="20"
                    value={replicarQtd}
                    onChange={(e) => setReplicarQtd(e.target.value)}
                    className="input-sm"
                  />
                  <input
                    type="number"
                    placeholder="Dist"
                    step="0.1"
                    value={replicarDist}
                    onChange={(e) => setReplicarDist(e.target.value)}
                    className="input-sm"
                  />
                  <select
                    value={replicarDir}
                    onChange={(e) => setReplicarDir(e.target.value)}
                    className="select-sm"
                  >
                    <option value="x">X</option>
                    <option value="y">Y</option>
                  </select>
                  <button
                    className="btn-sm btn-success"
                    onClick={() => handleReplicarFuro(
                      parseInt(replicarQtd),
                      parseFloat(replicarDist),
                      replicarDir
                    )}
                  >
                    Replicar
                  </button>
                </div>

                <button
                  className="btn-sm btn-danger full-width"
                  onClick={() => handleDeleteFuro(selectedFuro.tipo, selectedFuro.index)}
                >
                  Remover
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default EditorMPR;
